import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../firebaseConfig";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc } from "firebase/firestore";
import { safeGetDoc } from "../lib/safeGet";
import { clearSessionState } from "../lib/listState";

const AuthContext = createContext();

// Cache leve do perfil (só uid + userType) para não travar o roteamento caso a
// leitura do Firestore demore no mobile. Nunca é a fonte da verdade: serve
// apenas de fallback enquanto o getDoc real não responde.
const PROFILE_CACHE_KEY = "authProfileCache";
// Tempo máximo que esperamos o getDoc antes de liberar a tela. Passado esse
// limite, o app entra com o fallback e o getDoc segue rodando em background,
// corrigindo o usuário assim que responder.
const USER_LOAD_TIMEOUT_MS = 8000;

export function useAuth() {
  return useContext(AuthContext);
}

const readCachedProfile = (uid) => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.uid === uid ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedProfile = (uid, userType) => {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ uid, userType }));
  } catch {
    /* localStorage indisponível (ex.: Safari privado) — ignora */
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Faz a leitura do perfil no Firestore. Nunca rejeita: aplica o resultado via
  // setUser e resolve. Assim pode rodar em background sem quebrar o fluxo.
  const fetchUserData = async (authUser) => {
    try {
      const userDoc = await safeGetDoc(doc(db, "users", authUser.uid), "profile");
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const merged = {
          ...authUser,
          ...userData,
          // Manter userType como está no Firestore (pode ser string ou array)
          userType: userData.userType || "b2c",
        };
        setUser(merged);
        writeCachedProfile(authUser.uid, merged.userType);
      } else {
        setUser(authUser);
      }
    } catch (error) {
      console.error("Error loading user data:", error);
      setUser(authUser);
    }
  };

  // Força o SDK a materializar o token de auth ANTES de o app liberar as páginas
  // protegidas. Sem isto, no mobile (boot lento ou app retomado do segundo
  // plano) as páginas montam e anexam os onSnapshot antes de o Firestore ter o
  // token, e as regras avaliam com auth == null → permission-denied em VÁRIAS
  // abas ao mesmo tempo, deixando os spinners presos. getIdToken() devolve o
  // token em cache na hora quando ele é válido; só vai à rede se estiver
  // expirado — que é exatamente quando queremos esperar.
  const ensureAuthToken = async (authUser) => {
    try {
      await Promise.race([
        authUser.getIdToken(),
        new Promise((resolve) => setTimeout(resolve, USER_LOAD_TIMEOUT_MS)),
      ]);
    } catch (error) {
      console.warn("Não foi possível preparar o token de auth:", error);
    }
  };

  // Espera a leitura no máximo USER_LOAD_TIMEOUT_MS. Se estourar, libera a tela
  // com o userType em cache (para o roteamento não quebrar) e deixa a leitura
  // seguir em background — ela chama setUser de novo quando responder.
  const loadUserData = async (authUser) => {
    // Garante o token antes de qualquer listener protegido ser anexado.
    await ensureAuthToken(authUser);

    const fetchPromise = fetchUserData(authUser);
    let timeoutId;
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve("timeout"), USER_LOAD_TIMEOUT_MS);
    });

    const result = await Promise.race([
      fetchPromise.then(() => "done"),
      timeoutPromise,
    ]);
    clearTimeout(timeoutId);

    if (result === "timeout") {
      // Leitura demorou: entra com fallback para não ficar preso no spinner.
      const cached = readCachedProfile(authUser.uid);
      setUser(cached ? { ...authUser, userType: cached.userType } : authUser);
      // fetchPromise continua rodando e corrige o usuário ao completar.
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        await loadUserData(authUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signup = async (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      await loadUserData(userCredential.user);
      setLoading(false);
      return userCredential;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);
      setUser(null);
      // Filtros/página/rolagem das tabelas ficam na sessão da aba (sem escopo
      // por uid). Limpamos aqui para não vazar de uma conta para a próxima.
      clearSessionState();
    } finally {
      setLoading(false);
    }
  };

  // Força recarregar dados do usuário do Firestore
  const refreshUser = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      await loadUserData(currentUser);
    }
  };

  const value = {
    user,
    signup,
    login,
    logout,
    refreshUser,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
